import React, { useState, useEffect } from 'react';
import { FiSearch, FiPrinter, FiFileText, FiEye, FiPlus } from 'react-icons/fi';

import api from '../utils/api';
import toast from 'react-hot-toast';

const AddOrderModal = ({
  showCreateOrderModal,
  setShowCreateOrderModal,
  customerData,
  openFromPage,
}) => {
  const [newOrder, setNewOrder] = useState({
    customerName: customerData?.customerName || '',
    customerPhone: customerData?.customerMobile || '',
    orderType: 'dine-in',
    tableNumber: customerData?.tableNumber,
    items: [],
    paymentMethod: 'cash',
    gstNumber: ''
  });

  useEffect(() => {
    setNewOrder({
      customerName: customerData?.customerName || '',
      customerPhone: customerData?.customerMobile || '',
      orderType: 'dine-in',
      tableNumber: customerData?.tableNumber,
      items: customerData?.items || [],
      paymentMethod: 'cash',
      gstNumber: customerData?.gstNumber || ''
    })
  }, [customerData])

  const [availableTables, setAvailableTables] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [itemSearchTerm, setItemSearchTerm] = useState('');
  const [menuItems, setMenuItems] = useState([]);


  useEffect(() => {
    fetchAvailableTables();
    fetchCategories();
    fetchMenuItems();
  }, [customerData]);

  const fetchMenuItems = async () => {
    try {
      const response = await api.get('/vendor/menu-items');
      setMenuItems(response.data.data || []);
    } catch (error) {
      toast.error('Failed to fetch menu items');
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/vendor/categories');
      setCategories(response.data.data || []);
    } catch (error) {
      toast.error('Failed to fetch categories');
    }
  };

  const handleAddItem = (menuItem) => {
    const existingItem = newOrder.items.find(item => item.menuItemId === menuItem._id);
    if (existingItem) {
      setNewOrder({
        ...newOrder,
        items: newOrder.items.map(item =>
          item.menuItemId === menuItem._id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        )
      });
    } else {
      setNewOrder({
        ...newOrder,
        items: [...newOrder.items, {
          menuItemId: menuItem._id,
          name: menuItem.name,
          price: menuItem.discountPrice || menuItem.price,
          quantity: 1
        }]
      });
    }
  };

  const fetchAvailableTables = async () => {
    try {
      const response = await api.get('/vendor/tables');
      // Filter out tables that are currently open
      if (customerData?.customerMobile) {
        setAvailableTables(response.data.data);
      } else {
        const openTablesRes = await api.get('/vendor/open-tables');
        const openTableIds = openTablesRes.data.data?.map(ot => ot.tableId?._id) || [];
        const closedTables = response.data.data?.filter(table => !openTableIds.includes(table._id)) || [];
        setAvailableTables(closedTables);
      }

    } catch (error) {
      toast.error('Failed to fetch tables');
    }
  };

  const handleUpdateQuantity = (menuItemId, quantity) => {
    if (quantity <= 0) {
      handleRemoveItem(menuItemId);
      return;
    }
    setNewOrder({
      ...newOrder,
      items: newOrder.items.map(item =>
        item.menuItemId === menuItemId
          ? { ...item, quantity: parseInt(quantity) }
          : item
      )
    });
  };

  const handleRemoveItem = (menuItemId) => {
    setNewOrder({
      ...newOrder,
      items: newOrder.items.filter(item => item.menuItemId !== menuItemId)
    });
  };

  const calculateTotal = () => {
    return newOrder.items.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const filteredItems = menuItems.filter((item) => {
    const matchesCategory =
      !selectedCategory ||
      item.categoryId === selectedCategory ||
      item.categoryId?._id === selectedCategory;

    const matchesSearch =
      !itemSearchTerm ||
      item.name?.toLowerCase().includes(itemSearchTerm.toLowerCase());

    return matchesCategory && matchesSearch;
  });

  const handleCreateOrder = async () => {
    if (!newOrder.customerPhone || newOrder.customerPhone.length !== 10) {
      toast.error('Please enter valid 10-digit phone number');
      return;
    }
    if (newOrder.items.length === 0) {
      toast.error('Please add at least one item');
      return;
    }

    const tableNo = Number(newOrder.tableNumber);
    if (newOrder.orderType === 'dine-in' && isNaN(tableNo)) {
      toast.error('Invalid table number');
      return;
    }

    try {
      await api.post('/vendor/orders/create', newOrder);
      toast.success('Order created successfully');
      setShowCreateOrderModal(false);
      setItemSearchTerm(''); // Reset search term
      setNewOrder({
        customerName: '',
        customerPhone: '',
        orderType: 'dine-in',
        tableNumber: '',
        items: [],
        paymentMethod: 'cash',
        gstNumber: ''
      });
      // fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create order');
    }
  };




  if (!showCreateOrderModal) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-0 z-50">
      <div className="w-full h-full bg-white rounded-none shadow-2xl flex flex-col overflow-hidden">

        {/* HEADER */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50 sticky top-0 z-10">
          <h2 className="text-xl text-gray-800">
            Create New Order
          </h2>

          <button
            onClick={() => setShowCreateOrderModal(false)}
            className="text-gray-500 hover:text-red-500 text-2xl transition"
          >
            ×
          </button>
        </div>

        {/* BODY */}
        <div className="flex-1 min-h-0 p-2 overflow-y-auto custom-scroll">
          {/* DESKTOP 3 COLUMN LAYOUT */}
          <div className="hidden md:block h-full">
            <div className="grid grid-cols-[2.5fr_6fr_3.5fr] gap-2 h-full min-h-0">

              {/* COLUMN 1: CATEGORIES */}
              <div className="pr-2 border-r border-red-300 h-full flex flex-col min-h-0">
                <h3 className="text-sm mb-2 pb-1 border-b border-gray-200">
                  Categories
                </h3>

                <div className="space-y-2 overflow-y-auto custom-scroll flex-1 min-h-0">
                  {/* All Categories */}
                  <button
                    onClick={() => setSelectedCategory('')}
                    className={`w-full text-left border rounded-lg px-3 py-2 text-xs transition
                  ${!selectedCategory
                        ? "bg-blue-500 text-white border-blue-500 shadow"
                        : "bg-white hover:bg-blue-50 border-gray-300"
                      }`}
                  >
                    All Categories
                  </button>

                  {categories.map((category) => (
                    <button
                      key={category._id}
                      onClick={() => setSelectedCategory(category._id)}
                      className={`w-full text-left border rounded-lg px-2 py-2 text-sm transition
                    ${selectedCategory === category._id
                          ? "bg-blue-500 text-white border-blue-500 shadow"
                          : "bg-white hover:bg-blue-50 border-gray-300"
                        }`}
                    >
                      {category.name}
                    </button>
                  ))}
                  
                </div>
              </div>

              {/* COLUMN 2: ITEMS */}
              <div className="pr-2 border-r border-red-300 h-full flex flex-col min-h-0">
                <h3 className="text-sm mb-2 pb-1 border-b border-gray-200">
                  Menu Items
                </h3>
                <div className=" pr-2 flex flex-col h-full min-h-0">
                  {/* Header + Search + Items (scrollable) */}
                  <div className="flex-1 min-h-0 overflow-y-auto custom-scroll">
                    {/* Search */}
                    <div className="relative mb-2">
                      <FiSearch className="absolute left-3 top-2.5 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search items..."
                        value={itemSearchTerm}
                        onChange={(e) => setItemSearchTerm(e.target.value)}
                        className="w-full border rounded-lg pl-10 pr-4 py-2 focus:ring-1 focus:ring-grey-500 outline-none"
                      />
                    </div>

                    {/* Items Grid */}
                    <div className="grid grid-cols-3 gap-2">
                      {menuItems
                        .filter((item) => {
                          const matchesCategory =
                            !selectedCategory ||
                            item.categoryId === selectedCategory ||
                            item.categoryId?._id === selectedCategory;
                          const matchesSearch =
                            !itemSearchTerm ||
                            item.name?.toLowerCase().includes(itemSearchTerm.toLowerCase()) ||
                            item.itemCode?.toLowerCase().includes(itemSearchTerm.toLowerCase());
                          return matchesCategory && matchesSearch;
                        })
                        .map((item) => {
                          const isSelected = newOrder.items.some(orderItem => orderItem.menuItemId === item._id);
                          return (
                            <div
                              key={item._id}
                              onClick={() => handleAddItem(item)}
                              className={`border rounded-lg p-3 cursor-pointer transition-all ${isSelected
                                ? "bg-blue-50 border-blue-400 shadow-md"
                                : "bg-white hover:bg-gray-50"
                                }`}
                            >
                              <div className="flex flex-col gap-1">
                                <p className="text-xs font-medium truncate">{item.name}</p>
                                <p className="text-blue-500 text-sm font-semibold">
                                  ₹{item.discountPrice || item.price}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                    </div>

                    {/* No items fallback */}
                    {menuItems.filter(item => {
                      const matchesCategory =
                        !selectedCategory ||
                        item.categoryId === selectedCategory ||
                        item.categoryId?._id === selectedCategory;
                      const matchesSearch =
                        !itemSearchTerm ||
                        item.name?.toLowerCase().includes(itemSearchTerm.toLowerCase()) ||
                        item.itemCode?.toLowerCase().includes(itemSearchTerm.toLowerCase());
                      return matchesCategory && matchesSearch;
                    }).length === 0 && (
                        <div className="col-span-3 text-center py-8">
                          <div className="text-3xl mb-2">🔍</div>
                          <p className="text-gray-600 text-sm mb-1">No items found</p>
                          <p className="text-gray-400 text-xs">
                            {selectedCategory && itemSearchTerm
                              ? `No items found in this category matching "${itemSearchTerm}"`
                              : selectedCategory
                                ? 'No items available in this category'
                                : itemSearchTerm
                                  ? `No items matching "${itemSearchTerm}"`
                                  : 'No items available'
                            }
                          </p>
                        </div>
                      )}
                  </div>

                  {/* FORM SECTION - fixed at bottom */}
                  <div className="mt-auto border-t pt-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                      {/* Customer Phone */}
                      <div className="md:col-span-1">
                        <label className="block text-sm mb-1">Customer Phone *</label>
                        <input
                          type="tel"
                          maxLength={10}
                          value={newOrder.customerPhone}
                          onChange={(e) =>
                            setNewOrder({ ...newOrder, customerPhone: e.target.value.replace(/\D/g, "") })
                          }
                          placeholder="10-digit phone number"
                          className="w-full border rounded-lg px-3 py-2 focus:ring-1 focus:ring-grey-500 focus:border-grey-500 outline-none"
                        />
                      </div>

                      {/* Customer Name */}
                      <div className="md:col-span-1">
                        <label className="block text-sm mb-1">Customer Name</label>
                        <input
                          type="text"
                          value={newOrder.customerName}
                          onChange={(e) =>
                            setNewOrder({ ...newOrder, customerName: e.target.value })
                          }
                          placeholder="Optional"
                          className="w-full border rounded-lg px-3 py-2 focus:ring-1 focus:ring-grey-500 focus:border-grey-500 outline-none"
                        />
                      </div>

                      {/* Order Type */}
                      {openFromPage !== "billing" && (
                        <div className="md:col-span-1">
                          <label className="block text-sm mb-1">Order Type *</label>
                          <select
                            value={newOrder.orderType}
                            onChange={(e) =>
                              setNewOrder({ ...newOrder, orderType: e.target.value })
                            }
                            className="w-full text-sm border rounded-lg px-3 py-2 focus:ring-1 focus:ring-grey-500 outline-none"
                          >
                            <option value="dine-in">Dine-In</option>
                            <option value="pickup">Pickup</option>
                            <option value="delivery">Delivery</option>
                            <option value="vehicle">Vehicle</option>
                          </select>
                        </div>
                      )}

                      {/* Table Select */}
                      {newOrder.orderType === "dine-in" && openFromPage !== "billing" && (
                        <div className="md:col-span-1">
                          <label className="block text-sm mb-1">Select Table *</label>
                          <select
                            value={newOrder.tableNumber}
                            onChange={(e) =>
                              setNewOrder({ ...newOrder, tableNumber: e.target.value })
                            }
                            className="w-full text-sm border rounded-lg px-3 py-2 focus:ring-1 focus:ring-grey-500 outline-none"
                          >
                            <option value="">Select table</option>
                            {availableTables.map((table) => (
                              <option key={table._id} value={table.tableNumber} className="text-sm">
                                Table {table.tableNumber} ({table.seatingCapacity} seats)
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                    </div>
                  </div>
                </div>
              </div>

              {/* COLUMN 3: ORDER ITEMS */}
              <div className="flex flex-col h-full min-h-0">
                {/* Header */}
                <h3 className="text-sm mb-2 pb-1 border-b border-gray-200">
                  Order Items ({newOrder.items.length})
                </h3>

                {/* Items list - scrollable */}
                <div className="flex-1 min-h-0 overflow-y-auto divide-y custom-scroll">
                  {newOrder.items.length > 0 ? (
                    newOrder.items.map((item) => (
                      <div key={item.menuItemId} className="p-1">

                        {/* Name + Quantity Controls */}
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-xs truncate whitespace-nowrap">{item.name}</p>

                          <div className="flex items-center gap-1">
                            <button
                              onClick={() =>
                                handleUpdateQuantity(item.menuItemId, item.quantity - 1)
                              }
                              className="w-6 h-6 bg-gray-200 rounded text-sm"
                            >
                              -
                            </button>

                            <span className="text-xs w-6 text-center">{item.quantity}</span>

                            <button
                              onClick={() =>
                                handleUpdateQuantity(item.menuItemId, item.quantity + 1)
                              }
                              className="w-6 h-6 bg-blue-500 text-white rounded text-xs"
                            >
                              +
                            </button>
                          </div>
                        </div>

                        {/* Price */}
                        <div className="flex justify-between">
                          <span className="text-xs">
                            ₹{(item.price * item.quantity).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8">
                      <div className="text-3xl mb-2">🛒</div>
                      <p className="text-gray-600 font-semibold text-sm mb-1">No items added</p>
                      <p className="text-gray-400 text-xs">
                        Select items from the menu to add them to your order
                      </p>
                    </div>
                  )}
                </div>

                {/* Total - sticky at bottom */}
                <div className="border-t mt-2 pt-2 flex justify-between font-semibold text-sm pr-2">
                  <span>Total</span>
                  <span>₹{calculateTotal().toFixed(2)}</span>
                </div>

              </div>

            </div>
          </div>

          {/* MOBILE LAYOUT */}
          <div className="md:hidden">
            <div className="space-y-4">
              {/* Customer Form Section */}
              <div className="bg-white border rounded-lg p-4">
                <h3 className="text-sm font-semibold mb-3 text-gray-800">Customer Details</h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Customer Phone *</label>
                    <input
                      type="tel"
                      maxLength={10}
                      value={newOrder.customerPhone}
                      onChange={(e) =>
                        setNewOrder({ ...newOrder, customerPhone: e.target.value.replace(/\D/g, "") })
                      }
                      placeholder="10-digit phone number"
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Customer Name</label>
                    <input
                      type="text"
                      value={newOrder.customerName}
                      onChange={(e) =>
                        setNewOrder({ ...newOrder, customerName: e.target.value })
                      }
                      placeholder="Optional"
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                  </div>
                  {openFromPage !== "billing" && (
                    <>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Order Type *</label>
                        <select
                          value={newOrder.orderType}
                          onChange={(e) =>
                            setNewOrder({ ...newOrder, orderType: e.target.value })
                          }
                          className="w-full text-sm border rounded-lg px-3 py-2 focus:ring-1 focus:ring-blue-500 outline-none"
                        >
                          <option value="dine-in">Dine-In</option>
                          <option value="pickup">Pickup</option>
                          <option value="delivery">Delivery</option>
                          <option value="vehicle">Vehicle</option>
                        </select>
                      </div>
                      {newOrder.orderType === "dine-in" && (
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Select Table *</label>
                          <select
                            value={newOrder.tableNumber}
                            onChange={(e) =>
                              setNewOrder({ ...newOrder, tableNumber: e.target.value })
                            }
                            className="w-full text-sm border rounded-lg px-3 py-2 focus:ring-1 focus:ring-blue-500 outline-none"
                          >
                            <option value="">Select table</option>
                            {availableTables.map((table) => (
                              <option key={table._id} value={table.tableNumber}>
                                Table {table.tableNumber} ({table.seatingCapacity} seats)
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Categories Section */}
              <div className="bg-white border rounded-lg p-4">
                <h3 className="text-sm font-semibold mb-3 text-gray-800">Categories</h3>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setSelectedCategory('')}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
                      !selectedCategory
                        ? "bg-blue-500 text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    All
                  </button>
                  {categories.map((category) => (
                    <button
                      key={category._id}
                      onClick={() => setSelectedCategory(category._id)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
                        selectedCategory === category._id
                          ? "bg-blue-500 text-white"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                    >
                      {category.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Search Section */}
              <div className="bg-white border rounded-lg p-4">
                <div className="relative">
                  <FiSearch className="absolute left-3 top-2.5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search items..."
                    value={itemSearchTerm}
                    onChange={(e) => setItemSearchTerm(e.target.value)}
                    className="w-full border rounded-lg pl-10 pr-4 py-2 text-sm focus:ring-1 focus:ring-blue-500 outline-none"
                  />
                </div>
              </div>

              {/* Menu Items Section */}
              <div className="bg-white border rounded-lg p-4">
                <h3 className="text-sm font-semibold mb-3 text-gray-800">Menu Items</h3>
                <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto">
                  {filteredItems.map((item) => {
                    const isSelected = newOrder.items.some(orderItem => orderItem.menuItemId === item._id);
                    return (
                      <div
                        key={item._id}
                        onClick={() => handleAddItem(item)}
                        className={`border rounded-lg p-3 cursor-pointer transition-all ${
                          isSelected
                            ? "bg-blue-50 border-blue-400"
                            : "bg-white hover:bg-gray-50 border-gray-200"
                        }`}
                      >
                        <div className="flex flex-col gap-1">
                          <p className="text-xs font-medium truncate">{item.name}</p>
                          <p className="text-blue-500 text-sm font-semibold">
                            ₹{item.discountPrice || item.price}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {filteredItems.length === 0 && (
                  <div className="text-center py-8">
                    <div className="text-2xl mb-2">{"\ud83d\udd0d"}</div>
                    <p className="text-gray-600 text-sm">No items found</p>
                  </div>
                )}
              </div>

              {/* Order Items Section */}
              <div className="bg-white border rounded-lg p-4">
                <h3 className="text-sm font-semibold mb-3 text-gray-800">
                  Order Items ({newOrder.items.length})
                </h3>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {newOrder.items.length > 0 ? (
                    newOrder.items.map((item) => (
                      <div key={item.menuItemId} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <div className="flex-1">
                          <p className="text-xs font-medium truncate">{item.name}</p>
                          <p className="text-xs text-gray-600">
                            ₹{item.price} x {item.quantity} = ₹{(item.price * item.quantity).toFixed(2)}
                          </p>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() =>
                              handleUpdateQuantity(item.menuItemId, item.quantity - 1)
                            }
                            className="w-6 h-6 bg-gray-200 rounded text-xs"
                          >
                            -
                          </button>
                          <span className="text-xs w-6 text-center">{item.quantity}</span>
                          <button
                            onClick={() =>
                              handleUpdateQuantity(item.menuItemId, item.quantity + 1)
                            }
                            className="w-6 h-6 bg-blue-500 text-white rounded text-xs"
                          >
                            +
                          </button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-4">
                      <div className="text-2xl mb-2">{"\ud83d\uded2"}</div>
                      <p className="text-gray-600 text-sm">No items added</p>
                    </div>
                  )}
                </div>
                {newOrder.items.length > 0 && (
                  <div className="mt-3 pt-3 border-t flex justify-between font-semibold text-sm">
                    <span>Total</span>
                    <span>₹{calculateTotal().toFixed(2)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* FOOTER */}
        <div className="flex justify-end gap-3 p-4 border-t bg-gray-50 sticky bottom-0 z-10">
          <button
            onClick={() => setShowCreateOrderModal(false)}
            className="px-4 py-2 border rounded-lg hover:bg-gray-100"
          >
            Cancel
          </button>

          <button
            onClick={handleCreateOrder}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          >
            <FiPlus /> Create Order
          </button>
        </div>

      </div>
    </div>
  );
};

export default AddOrderModal;
